import { formatNumbers, tryParseFloat } from '../../../helpers/functions.tsx';
import { Users } from '../../../models/User.tsx';

export type AgentWagerLimitKey =
    | 'agentRegularBetMaxWager'
    | 'agentParlayMaxWager'
    | 'agentTeaserMaxWager'
    | 'agentLiveMaxWager'
    | 'agentPropsMaxWager'
    | 'agentContestMaxWager';

export type AgentWagerLimitsForm = Record<AgentWagerLimitKey, string>;

export const AGENT_WAGER_LIMIT_FIELDS: {
    key: AgentWagerLimitKey;
    label: string;
    icon: string;
    description: string;
}[] = [
    { key: 'agentRegularBetMaxWager', label: 'Regular', icon: '🎯', description: 'Max straight bet for this agent', },
    { key: 'agentParlayMaxWager', label: 'Parlay', icon: '🔗', description: 'Max parlay wager for this agent', },
    { key: 'agentTeaserMaxWager', label: 'Teaser', icon: '📊', description: 'Max teaser wager for this agent', },
    { key: 'agentLiveMaxWager', label: 'Live', icon: '⚡', description: 'Max live wager for this agent', },
    { key: 'agentPropsMaxWager', label: 'Props', icon: '⭐', description: 'Max props wager for this agent', },
    { key: 'agentContestMaxWager', label: 'Future', icon: '🏆', description: 'Max contest and futures wager for this agent', },
];

export const emptyAgentWagerLimits = (): AgentWagerLimitsForm => ({
    agentRegularBetMaxWager: 'Unlimited',
    agentParlayMaxWager: 'Unlimited',
    agentTeaserMaxWager: 'Unlimited',
    agentLiveMaxWager: 'Unlimited',
    agentPropsMaxWager: 'Unlimited',
    agentContestMaxWager: 'Unlimited',
});

export type WagerLimitKey =
    | 'regularBetMaxWager'
    | 'parlayMaxWager'
    | 'teaserMaxWager'
    | 'liveMaxWager'
    | 'propsMaxWager'
    | 'contestMaxWager';

export type PlayerPayoutLimitKey =
    | 'maxPayout'
    | 'maxFreePlayPayout'
    | 'maxFreePlayOdds'
    | 'maxOverallOdds';

export type PlayerLimitKey = WagerLimitKey | PlayerPayoutLimitKey;

export type LimitInputKind = 'money' | 'odds';

export type WagerLimitsValues = Record<PlayerLimitKey, number>;
export type WagerLimitsForm = Record<PlayerLimitKey, string>;

export type LimitField = {
    key: PlayerLimitKey;
    label: string;
    icon: string;
    description: string;
    kind: LimitInputKind;
};

export const WAGER_LIMIT_FIELDS: LimitField[] = [
    { key: 'regularBetMaxWager', label: 'Regular', icon: '🎯', description: 'Straight bets', kind: 'money' },
    { key: 'parlayMaxWager', label: 'Parlay', icon: '🔗', description: 'Multi-leg parlays', kind: 'money' },
    { key: 'teaserMaxWager', label: 'Teaser', icon: '📊', description: 'Teaser wagers', kind: 'money' },
    { key: 'liveMaxWager', label: 'Live', icon: '⚡', description: 'In-play betting', kind: 'money' },
    { key: 'propsMaxWager', label: 'Props', icon: '⭐', description: 'Player & game props', kind: 'money' },
    { key: 'contestMaxWager', label: 'Future', icon: '🏆', description: 'Contest & futures', kind: 'money' },
];

/** Shown when a player limit is missing or 0. */
export const PLAYER_LIMIT_NOT_SET = 'Not set';

export const PLAYER_PAYOUT_LIMIT_FIELDS: LimitField[] = [
    { key: 'maxPayout', label: 'Max Payout', icon: '💰', description: 'Overrides the agent payout. Not set keeps the agent limit', kind: 'money' },
    { key: 'maxFreePlayPayout', label: 'Max Free Play Win', icon: '🎁', description: 'Overrides the agent Free Play win. Not set keeps the agent limit', kind: 'money' },
    { key: 'maxFreePlayOdds', label: 'Max Free Play Odds', icon: '🎯', description: 'Overrides the agent Free Play odds. Not set keeps the agent limit', kind: 'odds' },
    { key: 'maxOverallOdds', label: 'Max Overall Odds', icon: '📈', description: 'Overrides the agent overall odds. Not set keeps the agent limit', kind: 'odds' },
];

export const ALL_PLAYER_LIMIT_FIELDS: LimitField[] = [
    ...WAGER_LIMIT_FIELDS,
    ...PLAYER_PAYOUT_LIMIT_FIELDS,
];

/** Default limits used for new players and as the All Players preset fallback. */
export const DEFAULT_WAGER_LIMITS_PRESET: WagerLimitsValues = {
    regularBetMaxWager: 1000,
    parlayMaxWager: 500,
    teaserMaxWager: 500,
    liveMaxWager: 1000,
    propsMaxWager: 250,
    contestMaxWager: 1000,
    maxPayout: 0,
    maxFreePlayPayout: 0,
    maxFreePlayOdds: 0,
    maxOverallOdds: 0,
};

export const emptyLimitsForm = (): WagerLimitsForm => ({
    regularBetMaxWager: '',
    parlayMaxWager: '',
    teaserMaxWager: '',
    liveMaxWager: '',
    propsMaxWager: '',
    contestMaxWager: '',
    maxPayout: '',
    maxFreePlayPayout: '',
    maxFreePlayOdds: '',
    maxOverallOdds: '',
});

export const formatWagerDisplay = (value: number): string =>
    value > 0 ? formatNumbers(value) : 'Unlimited';

const isUnsetLimitText = (value: string): boolean => {
    const trimmed = value.trim().toLowerCase();
    return trimmed === '' || trimmed === 'unlimited' || trimmed === 'not set' || trimmed === '0' || trimmed === '+0';
};

export const formatLimitDisplay = (_key: PlayerLimitKey, kind: LimitInputKind, value: number): string => {
    if (!(value > 0)) return PLAYER_LIMIT_NOT_SET;
    return kind === 'odds' ? `+${formatNumbers(value)}` : formatNumbers(value);
};

export const buildLimitsFormFromValues = (values: WagerLimitsValues): WagerLimitsForm => {
    const form = emptyLimitsForm();
    ALL_PLAYER_LIMIT_FIELDS.forEach(({ key, kind }) => {
        form[key] = formatLimitDisplay(key, kind, values[key] ?? 0);
    });
    return form;
};

const playerPayoutValue = (value: number | undefined): number => value ?? 0;

export const buildLimitsForm = (player: Users): WagerLimitsForm =>
    buildLimitsFormFromValues({
        regularBetMaxWager: player.regularBetMaxWager ?? 0,
        parlayMaxWager: player.parlayMaxWager ?? 0,
        teaserMaxWager: player.teaserMaxWager ?? 0,
        liveMaxWager: player.liveMaxWager ?? 0,
        propsMaxWager: player.propsMaxWager ?? 0,
        contestMaxWager: player.contestMaxWager ?? 0,
        maxPayout: playerPayoutValue(player.maxPayout),
        maxFreePlayPayout: playerPayoutValue(player.maxFreePlayPayout),
        maxFreePlayOdds: playerPayoutValue(player.maxFreePlayOdds),
        maxOverallOdds: playerPayoutValue(player.maxOverallOdds),
    });

export const parseWagerInput = (value: string): number => {
    const trimmed = value.trim().toLowerCase().replace(/,/g, '');
    if (isUnsetLimitText(trimmed) || trimmed === 'unlimited') return 0;
    return tryParseFloat(trimmed) ?? 0;
};

export const parseLimitsForm = (form: WagerLimitsForm): WagerLimitsValues => {
    const values = { ...DEFAULT_WAGER_LIMITS_PRESET };
    ALL_PLAYER_LIMIT_FIELDS.forEach(({ key, kind }) => {
        values[key] = kind === 'odds' ? parseOddsInput(form[key]) : parseWagerInput(form[key]);
    });
    return values;
};

export const formatLimitChange = (key: PlayerLimitKey, kind: LimitInputKind, value: number): string =>
    formatLimitDisplay(key, kind, value);

export const formatWagerInputLive = (value: string): string => {
    if (value === '') return '';
    if (/[a-zA-Z]/.test(value)) return value;

    const digits = value.replace(/[^\d]/g, '');
    if (digits === '') return '';
    return formatNumbers(digits);
};

/** Display American odds ceiling (e.g. 905 → "+905"). 0 = Unlimited. */
export const formatOddsDisplay = (value: number): string =>
    value > 0 ? `+${formatNumbers(value)}` : 'Unlimited';

export const parseOddsInput = (value: string): number => {
    const trimmed = value.trim().toLowerCase().replace(/,/g, '');
    if (isUnsetLimitText(trimmed)) return 0;
    const cleaned = trimmed.replace(/^\+/, '');
    return tryParseFloat(cleaned) ?? 0;
};

/** Live typing for odds fields: keep optional leading +, digits only. */
export const formatOddsInputLive = (value: string): string => {
    if (value === '') return '';
    if (/[a-zA-Z]/.test(value)) return value;

    const hasPlus = value.trimStart().startsWith('+');
    const digits = value.replace(/[^\d]/g, '');
    if (digits === '') return hasPlus ? '+' : '';
    return `${hasPlus ? '+' : ''}${formatNumbers(digits)}`;
};

export const getStatusClass = (status: string) => {
    const normalized = status?.toLowerCase() ?? '';
    if (normalized === 'active') return 'active';
    if (normalized === 'read-only') return 'readonly';
    return 'disabled';
};
